/**
 * Error Boundary Component
 *
 * Catches JavaScript errors anywhere in the child component tree,
 * logs the error, and displays a fallback UI instead of crashing.
 *
 * Usage:
 *   <ErrorBoundary>
 *     <App />
 *   </ErrorBoundary>
 */

import React, { Component, ReactNode } from 'react';
import { Button, Card, Stack, spacing, useTheme, fontSize } from '@fti/design-system';

interface ErrorBoundaryProps {
  /** Child components to render */
  children: ReactNode;
  /** Optional custom fallback UI */
  fallback?: (error: Error, errorInfo: React.ErrorInfo) => ReactNode;
}

interface ErrorBoundaryState {
  /** Whether an error has been caught */
  hasError: boolean;
  /** The caught error, if any */
  error: Error | null;
  /** React error info with component stack */
  errorInfo: React.ErrorInfo | null;
}

/**
 * Default error UI component (functional to use hooks)
 */
interface DefaultErrorUIProps {
  error: Error;
  errorInfo: React.ErrorInfo | null;
  onReset: () => void;
}

const DefaultErrorUI: React.FC<DefaultErrorUIProps> = ({ error, errorInfo, onReset }) => {
  const { theme } = useTheme();

  // Theme-aware colors from design system
  const bgColor = theme.colors.background.default;
  const textColor = theme.colors.text.primary;
  const mutedColor = theme.colors.text.secondary;
  const errorColor = theme.colors.semantic.error.default;
  const cardBgColor = theme.colors.background.paper;
  const borderColor = theme.colors.border.default;

  return (
    <div style={{
      padding: spacing.lg,
      maxWidth: '800px',
      margin: `${spacing.xl} auto`,
      background: bgColor
    }}>
      <Card variant="outlined" style={{ borderColor: errorColor }}>
        <Card.Body>
          <Stack gap="md">
            <h1 style={{ color: errorColor, marginTop: 0 }}>
              Something went wrong
            </h1>
            <p style={{ color: mutedColor }}>
              An unexpected error occurred. Please try refreshing the page.
            </p>

            <details style={{ marginTop: spacing.md }}>
              <summary style={{ cursor: 'pointer', fontWeight: 'bold', color: textColor }}>
                Error Details
              </summary>
              <pre style={{
                marginTop: spacing.sm,
                padding: spacing.sm,
                backgroundColor: cardBgColor,
                border: `1px solid ${borderColor}`,
                borderRadius: '4px',
                overflow: 'auto',
                fontSize: fontSize.xs,
                color: textColor
              }}>
                {error.toString()}
                {errorInfo && errorInfo.componentStack}
              </pre>
            </details>

            <Button
              variant="solid"
              color="primary"
              onClick={onReset}
              style={{ marginTop: spacing.md }}
            >
              Try Again
            </Button>
          </Stack>
        </Card.Body>
      </Card>
    </div>
  );
};

/**
 * Error boundary component that catches rendering errors
 *
 * Implements React error boundary lifecycle methods to gracefully
 * handle errors in the component tree.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  /**
   * Update state when an error is caught
   */
  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  /**
   * Log error details to console
   */
  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error('Error caught by ErrorBoundary:', error, errorInfo);
    this.setState({ errorInfo });
  }

  /**
   * Reset error boundary state
   */
  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  render(): ReactNode {
    const { hasError, error, errorInfo } = this.state;
    const { children, fallback } = this.props;

    if (hasError && error) {
      // Use custom fallback if provided
      if (fallback && errorInfo) {
        return fallback(error, errorInfo);
      }

      // Default error UI using theme-aware component
      return (
        <DefaultErrorUI
          error={error}
          errorInfo={errorInfo}
          onReset={this.handleReset}
        />
      );
    }

    return children;
  }
}
